// Pure, React-free helpers that drive the post-auth action registry. Call sites
// enqueue an action's payload before triggering auth; after auth completes
// (inline, or in the OAuth callback) the pending actions are drained.

import type {PostAuthAction} from './types';

/**
 * Persist an action's payload so it survives a navigation (e.g. an OAuth
 * redirect) and can be drained once the user is authenticated.
 */
export function enqueuePostAuthAction<T>(action: PostAuthAction<T>, payload: T): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(action.key, JSON.stringify(payload));
}

/**
 * Drain every pending action. Each payload is claimed (removed) before running
 * so a second drain can't double-execute. Each action owns its own error
 * handling — a thrown error is swallowed here so one failure can't block the rest.
 */
export async function drainPostAuthActions(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- heterogeneous payload types; payloads are untyped JSON at runtime.
  actions: readonly PostAuthAction<any>[],
): Promise<void> {
  if (typeof window === 'undefined') return;
  for (const action of actions) {
    const raw = localStorage.getItem(action.key);
    if (!raw) continue;
    // Claim the payload before running so a second drain can't pick it up.
    localStorage.removeItem(action.key);

    let payload: unknown;
    try {
      payload = JSON.parse(raw);
    } catch {
      continue;
    }

    try {
      await action.run(payload);
    } catch {
      // Actions surface their own user-facing errors; never let one block the rest.
    }
  }
}
